; (() => {
	const PANEL_TRANSITION_MS = 280

	function initCardSliderInstance(root) {
		if (!root || root.dataset.cardSliderInitialized === 'true') return
		root.dataset.cardSliderInitialized = 'true'

		const track = root.querySelector('.wus-ingredients__track')
		if (!track) return

		const cards = Array.from(root.querySelectorAll('.wus-ingredient[data-card-slider-card]'))
		if (!cards.length) return

		cards.forEach((card) => setupCard(root, track, cards, card))

		const btnPrev = root.querySelector('.slider__control.control_prev')
		const btnNext = root.querySelector('.slider__control.control_next')
		setupControls(root, track, cards, btnPrev, btnNext)

		const onResize = debounce(() => {
			const isMobile = window.matchMedia('(max-width: 1023px)').matches
			if (!isMobile) return

			const openCard = root.querySelector('.wus-ingredient.is-open[data-card-slider-card]')
			if (!openCard) return

			const panel = openCard.querySelector('[data-card-slider-panel]')
			if (!panel) return

			updateMobilePanelHeight(panel)
		}, 150)

		window.addEventListener('resize', onResize)
		root._cardSliderOnResize = onResize
	}

	function setupCard(root, track, cards, card) {
		const toggles = Array.from(card.querySelectorAll('[data-card-slider-toggle]'))
		const panel = card.querySelector('[data-card-slider-panel]')
		if (!toggles.length || !panel) return

		const isInitiallyOpen = panel.getAttribute('data-state') === 'open'
		setPanelState(card, panel, toggles, isInitiallyOpen)

		if (isInitiallyOpen && isStackedViewport()) {
			requestAnimationFrame(() => {
				updateMobilePanelHeight(panel)
			})
		}

		toggles.forEach((toggle) => {
			toggle.addEventListener('click', () => {
				const isOpen = panel.getAttribute('data-state') === 'open'

				if (isOpen) {
					closePanel(card, panel, toggles)
					return
				}

				openCardAndSnap(root, track, card, panel, toggles)
			})
		})
	}

	function openCardAndSnap(root, track, card, panel, toggles) {
		const hadClosedOthers = closeOtherPanels(root, card)
		const isLastCard =
			card === track.querySelector('.wus-ingredient[data-card-slider-card]:last-child')

		requestAnimationFrame(() => {
			openPanel(card, panel, toggles)

			if (isStackedViewport()) {
				requestAnimationFrame(() => {
					updateMobilePanelHeight(panel)

					const snapDelay = hadClosedOthers ? PANEL_TRANSITION_MS : 0
					window.setTimeout(() => {
						snapCardIntoView(track, card, 'smooth')
					}, snapDelay)
				})
				return
			}

			let snapDelay = 0
			let snapBehavior = 'smooth'

			if (hadClosedOthers) {
				snapDelay = PANEL_TRANSITION_MS
			} else if (isLastCard) {
				snapDelay = 300
				snapBehavior = 'auto'
			}

			window.setTimeout(() => {
				snapCardIntoView(track, card, snapBehavior)
			}, snapDelay)
		})
	}

	function openPanel(card, panel, toggles) {
		setPanelState(card, panel, toggles, true)
	}

	function closePanel(card, panel, toggles) {
		setPanelState(card, panel, toggles, false)
		panel.style.setProperty('--wus-card-slider-panel-height', '0px')
	}

	function closeOtherPanels(root, exceptCard) {
		const openCards = Array.from(
			root.querySelectorAll('.wus-ingredient.is-open[data-card-slider-card]')
		)

		let closedAny = false

		openCards.forEach((card) => {
			if (exceptCard && card === exceptCard) return

			const panel = card.querySelector('[data-card-slider-panel]')
			const toggles = Array.from(card.querySelectorAll('[data-card-slider-toggle]'))
			if (!panel || !toggles.length) return

			setPanelState(card, panel, toggles, false)
			panel.style.setProperty('--wus-card-slider-panel-height', '0px')
			closedAny = true
		})

		return closedAny
	}

	function closeAllPanels(root) {
		closeOtherPanels(root, null)
	}

	function setPanelState(card, panel, toggles, open) {
		panel.setAttribute('data-state', open ? 'open' : 'closed')

		toggles.forEach((toggle) => {
			toggle.setAttribute('aria-expanded', String(open))
		})

		card.classList.toggle('is-open', open)
	}

	function updateMobilePanelHeight(panel) {
		const inner = panel.querySelector('.wus-ingredient__panel-inner')
		if (!inner) return

		const styles = window.getComputedStyle(inner)
		const paddingTop = parseFloat(styles.paddingTop) || 0
		const paddingBottom = parseFloat(styles.paddingBottom) || 0
		const target = inner.scrollHeight + paddingTop + paddingBottom

		panel.style.setProperty('--wus-card-slider-panel-height', `${target}px`)
	}

	function snapCardIntoView(track, card, behavior = 'smooth') {
		card.scrollIntoView({
			behavior,
			inline: 'start',
			block: 'nearest',
		})
	}

	function setupControls(root, track, cards, btnPrev, btnNext) {
		let activeIndex = getClosestCardIndex(track, cards)

		function updateDisabledStates() {
			if (btnPrev) btnPrev.disabled = activeIndex <= 0
			if (btnNext) btnNext.disabled = activeIndex >= cards.length - 1
		}

		function scrollToIndex(index) {
			closeAllPanels(root)

			activeIndex = clamp(index, 0, cards.length - 1)
			const el = cards[activeIndex]
			if (!el) return

			requestAnimationFrame(() => {
				el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
				updateDisabledStates()
			})
		}

		if (btnPrev) btnPrev.addEventListener('click', () => scrollToIndex(activeIndex - 1))
		if (btnNext) btnNext.addEventListener('click', () => scrollToIndex(activeIndex + 1))

		const onScroll = debounce(() => {
			activeIndex = getClosestCardIndex(track, cards)
			updateDisabledStates()
		}, 80)

		track.addEventListener('scroll', onScroll, { passive: true })
		updateDisabledStates()
	}

	function getClosestCardIndex(track, cards) {
		const trackLeft = track.getBoundingClientRect().left
		let bestIndex = 0
		let bestDist = Infinity

		cards.forEach((card, idx) => {
			const left = card.getBoundingClientRect().left
			const dist = Math.abs(left - trackLeft)
			if (dist < bestDist) {
				bestDist = dist
				bestIndex = idx
			}
		})

		return bestIndex
	}

	function isStackedViewport() {
		return window.matchMedia('(max-width: 1023px)').matches
	}

	function clamp(n, min, max) {
		return Math.max(min, Math.min(max, n))
	}

	function debounce(fn, wait) {
		let t
		return (...args) => {
			window.clearTimeout(t)
			t = window.setTimeout(() => fn(...args), wait)
		}
	}

	function initCardSliders(rootEl) {
		const roots = rootEl
			? rootEl.querySelectorAll('[data-module="card-slider"]')
			: document.querySelectorAll('[data-module="card-slider"]')

		if (!roots.length) return
		roots.forEach((root) => initCardSliderInstance(root))
	}

	const boot = () => initCardSliders()

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot)
	} else {
		boot()
	}

	document.addEventListener('shopify:section:load', (event) => {
		initCardSliders(event.target)
	})

	document.addEventListener('shopify:section:unload', (event) => {
		const section = event.target
		if (!section?.querySelectorAll) return

		section.querySelectorAll('[data-module="card-slider"]').forEach((root) => {
			delete root.dataset.cardSliderInitialized

			if (root._cardSliderOnResize) {
				window.removeEventListener('resize', root._cardSliderOnResize)
				delete root._cardSliderOnResize
			}
		})
	})
})()